import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const MODE_ENCODE = "encode";
const MODE_DECODE = "decode";

const FORMAT_NAMED = "named";
const FORMAT_NUMERIC = "numeric";
const FORMAT_HEX = "hex";

const SCOPE_HTML_ONLY = "html-only";
const SCOPE_NON_ASCII = "non-ascii";

const NAMED_BY_CODEPOINT = {
  34: "&quot;",
  38: "&amp;",
  39: "&apos;",
  60: "&lt;",
  62: "&gt;",
  160: "&nbsp;",
  161: "&iexcl;",
  162: "&cent;",
  163: "&pound;",
  164: "&curren;",
  165: "&yen;",
  166: "&brvbar;",
  167: "&sect;",
  168: "&uml;",
  169: "&copy;",
  170: "&ordf;",
  171: "&laquo;",
  172: "&not;",
  173: "&shy;",
  174: "&reg;",
  175: "&macr;",
  176: "&deg;",
  177: "&plusmn;",
  178: "&sup2;",
  179: "&sup3;",
  180: "&acute;",
  181: "&micro;",
  182: "&para;",
  183: "&middot;",
  184: "&cedil;",
  185: "&sup1;",
  186: "&ordm;",
  187: "&raquo;",
  188: "&frac14;",
  189: "&frac12;",
  190: "&frac34;",
  191: "&iquest;",
  215: "&times;",
  247: "&divide;",
  338: "&OElig;",
  339: "&oelig;",
  352: "&Scaron;",
  353: "&scaron;",
  376: "&Yuml;",
  402: "&fnof;",
  710: "&circ;",
  732: "&tilde;",
  8194: "&ensp;",
  8195: "&emsp;",
  8201: "&thinsp;",
  8211: "&ndash;",
  8212: "&mdash;",
  8216: "&lsquo;",
  8217: "&rsquo;",
  8218: "&sbquo;",
  8220: "&ldquo;",
  8221: "&rdquo;",
  8222: "&bdquo;",
  8224: "&dagger;",
  8225: "&Dagger;",
  8226: "&bull;",
  8230: "&hellip;",
  8240: "&permil;",
  8242: "&prime;",
  8243: "&Prime;",
  8249: "&lsaquo;",
  8250: "&rsaquo;",
  8364: "&euro;",
  8482: "&trade;",
};

const HTML_SPECIAL_CODEPOINTS = new Set([34, 38, 39, 60, 62]);

const ENTITY_PATTERN = /&(?:[a-zA-Z][a-zA-Z0-9]*|#\d+|#[xX][0-9a-fA-F]+);/g;

const countChars = (s) => (s ? [...s].length : 0);

const countEntities = (s) => (s ? (s.match(ENTITY_PATTERN) || []).length : 0);

const formatEntity = (codePoint, format) => {
  if (format === FORMAT_NAMED && NAMED_BY_CODEPOINT[codePoint]) {
    return NAMED_BY_CODEPOINT[codePoint];
  }
  if (format === FORMAT_HEX) {
    return `&#x${codePoint.toString(16).toUpperCase()};`;
  }
  return `&#${codePoint};`;
};

const encodeText = (text, format, scope) => {
  let result = "";
  for (const ch of text) {
    const codePoint = ch.codePointAt(0);
    const isHtmlSpecial = HTML_SPECIAL_CODEPOINTS.has(codePoint);
    const isNonAscii = codePoint > 126 || codePoint < 32;
    const shouldEncode =
      isHtmlSpecial ||
      (scope === SCOPE_NON_ASCII && isNonAscii && codePoint !== 9 && codePoint !== 10 && codePoint !== 13);
    if (shouldEncode) {
      result += formatEntity(codePoint, format);
    } else {
      result += ch;
    }
  }
  return result;
};

const decodeText = (input) => {
  if (typeof DOMParser === "undefined") {
    throw new Error("dom_parser_unavailable");
  }
  const doc = new DOMParser().parseFromString(
    `<!doctype html><body>${input}`,
    "text/html",
  );
  return doc.body.textContent ?? "";
};

const DEFAULT_INPUT = '<a href="https://quto.dev?x=1&y=2">Quto.dev — Quick Tools</a>';

const HtmlEntityCodec = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE_ENCODE);
  const [format, setFormat] = useState(FORMAT_NAMED);
  const [scope, setScope] = useState(SCOPE_HTML_ONLY);
  const [input, setInput] = useState(DEFAULT_INPUT);

  const { output, error } = useMemo(() => {
    if (input === "") return { output: "", error: null };
    try {
      const result =
        mode === MODE_ENCODE
          ? encodeText(input, format, scope)
          : decodeText(input);
      return { output: result, error: null };
    } catch {
      return {
        output: "",
        error:
          mode === MODE_ENCODE
            ? "html_entity_codec_encode_error"
            : "html_entity_codec_decode_error",
      };
    }
  }, [input, mode, format, scope]);

  const inputCharCount = useMemo(() => countChars(input), [input]);
  const outputCharCount = useMemo(() => countChars(output), [output]);
  const entityCount = useMemo(
    () =>
      mode === MODE_ENCODE ? countEntities(output) : countEntities(input),
    [mode, input, output],
  );

  const swap = () => {
    if (!output || error) {
      toast.error(t("html_entity_codec_nothing_to_swap"));
      return;
    }
    setInput(output);
    setMode(mode === MODE_ENCODE ? MODE_DECODE : MODE_ENCODE);
  };

  const copy = async () => {
    if (!output) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setInput("");

  const inputLabelKey =
    mode === MODE_ENCODE
      ? "html_entity_codec_input_text_label"
      : "html_entity_codec_input_html_label";
  const inputPlaceholderKey =
    mode === MODE_ENCODE
      ? "html_entity_codec_input_text_placeholder"
      : "html_entity_codec_input_html_placeholder";
  const outputLabelKey =
    mode === MODE_ENCODE
      ? "html_entity_codec_output_html_label"
      : "html_entity_codec_output_text_label";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("html_entity_codec_title")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("html_entity_codec_intro")}
              </p>
            </div>

            <div className="flex flex-wrap items-start gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("html_entity_codec_mode_label")}
                </label>
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <Radio.Button value={MODE_ENCODE}>
                    {t("html_entity_codec_mode_encode")}
                  </Radio.Button>
                  <Radio.Button value={MODE_DECODE}>
                    {t("html_entity_codec_mode_decode")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              {mode === MODE_ENCODE && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      {t("html_entity_codec_format_label")}
                    </label>
                    <Radio.Group
                      value={format}
                      onChange={(e) => setFormat(e.target.value)}
                    >
                      <Radio.Button value={FORMAT_NAMED}>
                        {t("html_entity_codec_format_named")}
                      </Radio.Button>
                      <Radio.Button value={FORMAT_NUMERIC}>
                        {t("html_entity_codec_format_numeric")}
                      </Radio.Button>
                      <Radio.Button value={FORMAT_HEX}>
                        {t("html_entity_codec_format_hex")}
                      </Radio.Button>
                    </Radio.Group>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      {t("html_entity_codec_scope_label")}
                    </label>
                    <Radio.Group
                      value={scope}
                      onChange={(e) => setScope(e.target.value)}
                    >
                      <Radio.Button value={SCOPE_HTML_ONLY}>
                        {t("html_entity_codec_scope_html_only")}
                      </Radio.Button>
                      <Radio.Button value={SCOPE_NON_ASCII}>
                        {t("html_entity_codec_scope_non_ascii")}
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t(inputLabelKey)}
              </label>
              <textarea
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t(inputPlaceholderKey)}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <div className="flex flex-wrap gap-3 items-center">
                <StandardButton
                  onClick={swap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("html_entity_codec_swap")}
                </StandardButton>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("html_entity_codec_clear")}
                </StandardButton>
                {input && (
                  <span className="text-xs text-gray-500">
                    {t("html_entity_codec_input_char_count", {
                      count: inputCharCount,
                    })}
                  </span>
                )}
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{t(error)}</p>}

            {!error && output && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-indigo-700">
                    {t(outputLabelKey)}
                  </strong>
                  <StandardButton
                    onClick={copy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
                  {output}
                </p>
                <p className="mt-2 text-xs text-indigo-700/80">
                  {t("html_entity_codec_output_char_count", {
                    count: outputCharCount,
                  })}
                  {" · "}
                  {t(
                    mode === MODE_ENCODE
                      ? "html_entity_codec_entities_encoded"
                      : "html_entity_codec_entities_decoded",
                    { count: entityCount },
                  )}
                </p>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("html_entity_codec_note_named")}</p>
              <p>{t("html_entity_codec_note_decode")}</p>
              <p>{t("html_entity_codec_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default HtmlEntityCodec;
